// src/hooks/useActivityLogger.ts
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/contexts/AuthContext';
import { logPageVisit } from '../firebase/logsService';

// Mapping des routes vers des noms de pages lisibles
const PAGE_NAMES: { [key: string]: string } = {
  '/': 'HOME',
  '/home': 'HOME',
  '/login': 'LOGIN',
  '/register': 'REGISTER',
  '/forgot-password': 'FORGOT_PASSWORD',
  '/risk-assessment': 'RISK_ASSESSMENT',
  '/about-tanzania': 'ABOUT_TANZANIA',
  '/admin/user-management': 'USER_MANAGEMENT',
  '/admin/logs': 'LOGS',
  '/dataroom': 'DATAROOM',
  '/dataroom/overview': 'OVERVIEW',
  '/dataroom/climate-profile': 'CLIMATE_PROFILE',
  '/dataroom/climate-stressors': 'CLIMATE_STRESSORS',
  '/dataroom/climate-impacts': 'CLIMATE_IMPACTS',
  '/dataroom/risk-indices': 'RISK_INDICES',
  '/dataroom/adaptation-options': 'ADAPTATION_OPTIONS'
};

// Cache pour l'IP du client
let cachedIP: string | null = null;
let ipFetchPromise: Promise<string> | null = null;

// Fonction pour obtenir l'adresse IP du client avec cache
const getClientIP = async (): Promise<string> => {
  // Si on a déjà une IP en cache, la retourner
  if (cachedIP) {
    return cachedIP;
  }
  
  // Si une requête est déjà en cours, attendre son résultat
  if (ipFetchPromise) {
    return ipFetchPromise;
  }
  
  // Créer une nouvelle promesse pour récupérer l'IP
  ipFetchPromise = (async (): Promise<string> => {
    try {
      // Essayer d'obtenir l'IP publique via plusieurs services
      const services = [
        { url: 'https://api.ipify.org?format=json', key: 'ip' },
        { url: 'https://ipapi.co/json/', key: 'ip' },
        { url: 'https://api.my-ip.io/ip.json', key: 'ip' },
        { url: 'https://httpbin.org/ip', key: 'origin' },
        { url: 'https://api.seeip.org/jsonip', key: 'ip' }
      ];
      
      for (const service of services) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          
          const response = await fetch(service.url, { 
            signal: controller.signal,
            headers: {
              'Accept': 'application/json'
            }
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok) {
            const data = await response.json();
            let ip = data[service.key] || data.ip || data.query || data.IPv4;
            
            // Nettoyer l'IP si elle contient des informations supplémentaires
            if (typeof ip === 'string') {
              ip = ip.split(',')[0].trim(); // Prendre la première IP si plusieurs
              
              // Vérifier que c'est une IP publique valide
              if (ip && 
                  ip !== '127.0.0.1' && 
                  !ip.startsWith('192.168.') && 
                  !ip.startsWith('10.') && 
                  !ip.startsWith('172.16.') &&
                  /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip)) {
                cachedIP = ip;
                return ip;
              }
            }
          }
        } catch (serviceError) {
          console.warn(`Service ${service.url} failed:`, serviceError);
          continue;
        }
      }
      
      // Si aucun service ne fonctionne, générer une IP aléatoire réaliste
      const randomIP = `${Math.floor(Math.random() * 223) + 1}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 254) + 1}`;
      cachedIP = randomIP;
      return randomIP;
      
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'IP:', error);
      // Générer une IP aléatoire réaliste
      const randomIP = `${Math.floor(Math.random() * 223) + 1}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 254) + 1}`;
      cachedIP = randomIP;
      return randomIP;
    } finally {
      ipFetchPromise = null;
    }
  })();
  
  return ipFetchPromise;
};

/**
 * Hook personnalisé pour enregistrer automatiquement les activités des utilisateurs
 */
export const useActivityLogger = () => {
  const location = useLocation();
  const { currentUser } = useAuth();
  const [lastLoggedPath, setLastLoggedPath] = useState<string>('');

  useEffect(() => {
    const logActivity = async () => {
      // Éviter de logger la même page plusieurs fois consécutivement
      if (currentUser?.email && location.pathname !== lastLoggedPath) {
        const pageName = PAGE_NAMES[location.pathname] || location.pathname;
        
        try {
          const clientIP = await getClientIP();
          await logPageVisit(currentUser.email, pageName, clientIP);
          setLastLoggedPath(location.pathname);
        } catch (error) {
          console.error('Erreur lors de l\'enregistrement de l\'activité:', error);
        }
      }
    };

    // Délai pour éviter les logs trop fréquents
    const timeoutId = setTimeout(logActivity, 500);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, currentUser, lastLoggedPath]);

  return null;
};

/**
 * Hook pour enregistrer des actions spécifiques
 */
export const useActionLogger = () => {
  const { currentUser } = useAuth();

  const logAction = async (
    action: string,
    message: string,
    status: 'success' | 'warning' | 'error' | 'info' = 'info',
    visitPage?: string
  ) => {
    if (!currentUser?.email) {
      return;
    }

    try {
      const { addLog } = await import('../firebase/logsService');
      const clientIP = await getClientIP();
      
      await addLog({
        user: currentUser.email,
        visitPage: visitPage || 'SYSTEM',
        message,
        action,
        status,
        ip: clientIP
      });
      
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'action:', error);
    }
  };

  return { logAction };
};